import { CircularProgress } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { message } from "antd";
import React, { useEffect, useState } from "react";
import { IoIosCloseCircle } from "react-icons/io";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { useDispatch, useSelector } from "react-redux";
import { updateShowSendPaymentLinkDrawer } from "../../../reducer/bookingReducer";
import { paymentLinkService } from "../../../services/paymentLinkService";
import CustomTextField from "../../common/InputField/Input";

export default function SendPaymentLinkDrawer() {
  const {
    showSendPaymentLinkDrawer,
    bookingDetails,
    responseDetails,
    bookingHotelId,
    invoiceDetails,
  } = useSelector((store) => store.booking);

  // Use responseDetails.bookingDetails if bookingDetails is not available
  const actualBookingDetails =
    bookingDetails || responseDetails?.bookingDetails;

  const dispatch = useDispatch();

  const [amount, setAmount] = React.useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [loading, setLoading] = React.useState(false);
  const [phonePreFilled, setPhonePreFilled] = useState(false);
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const [email, setEmail] = useState("");

  // useEffect for initial data population when drawer opens
  useEffect(() => {
    if (!showSendPaymentLinkDrawer || !actualBookingDetails) return;

    // Reset form state when drawer first opens
    setPhoneValue("");
    setPhoneNumber("");
    setCountryCode("+91");
    setAmount("");
    setPhonePreFilled(false);
    setEmail("");

    // Pre-fill phone and email after a brief delay to ensure component is ready
    setTimeout(() => {
      // Get phone and email from booking details
      let customerPhone = actualBookingDetails.phone || "";
      let customerEmail = actualBookingDetails.email || "";

      // If not found, check customer details in responseDetails
      if (responseDetails?.customerDetails?.[0]) {
        const customer = responseDetails.customerDetails[0];
        if (!customerPhone) {
          customerPhone =
            customer.phone || customer.contactDetails?.phoneNumber || "";
        }
        if (!customerEmail) {
          customerEmail =
            customer.email || customer.contactDetails?.email || "";
        }
      }

      // Set email if available
      if (customerEmail) {
        setEmail(customerEmail);
      }

      // Set phone if available
      if (customerPhone) {
        if (customerPhone.startsWith("+")) {
          setPhoneValue(customerPhone);
          const countryCodeMatch = customerPhone.match(/^\+\d{1,4}/);
          if (countryCodeMatch) {
            const extractedCountryCode = countryCodeMatch[0];
            const extractedPhone = customerPhone.replace(
              extractedCountryCode,
              ""
            );
            setCountryCode(extractedCountryCode);
            setPhoneNumber(extractedPhone);
          }
        } else {
          // From data analysis, customer has countryCode +91, so use that
          const customerCountryCode =
            responseDetails?.customerDetails?.[0]?.contactDetails
              ?.countryCode || "+91";
          const fullPhone = customerCountryCode + customerPhone;
          setCountryCode(customerCountryCode);
          setPhoneNumber(customerPhone);
          setPhoneValue(fullPhone);
        }
        setPhonePreFilled(true);
        setPhoneInputKey((prev) => prev + 1);
      }
    }, 100);
  }, [showSendPaymentLinkDrawer, actualBookingDetails, responseDetails]);

  // useEffect for amount pre-filling - calculate balance due
  useEffect(() => {
    if (!showSendPaymentLinkDrawer || !invoiceDetails || amount) return;

    if (Array.isArray(invoiceDetails)) {
      const roomBill = invoiceDetails.find(
        (inv) => inv.invoiceType === "roomBill"
      );
      if (roomBill && responseDetails?.paymentDetails) {
        // Calculate balance due: total - paid amount
        const totalAmount = roomBill.total || 0;
        const totalPaid = responseDetails.paymentDetails.reduce(
          (sum, payment) => sum + (payment.amount || 0),
          0
        );
        const balanceDue = totalAmount - totalPaid;

        if (balanceDue > 0) {
          setAmount(Number(balanceDue?.toFixed(2)).toString());
        } else if (totalAmount > 0) {
          setAmount(Number(totalAmount?.toFixed(2)).toString());
        }
      }
    }
  }, [showSendPaymentLinkDrawer, invoiceDetails, responseDetails, amount]);

  const handlePhoneChange = (value, country) => {
    const parts = country.inputValue.split(" ");
    let newCountryCode = parts?.[0];

    const joinedString = parts
      ?.slice(1)
      ?.map((str) => str?.replace(/\D/g, "")) // Remove non-numeric characters
      ?.join("");

    setPhoneNumber(joinedString);
    setPhoneValue(value);
    setCountryCode(newCountryCode);
  };

  const handleClose = () => {
    setAmount("");
    setPhoneValue("");
    setPhoneNumber("");
    setCountryCode("+91");
    setLoading(false);
    setPhonePreFilled(false);
    setPhoneInputKey(0);
    setEmail("");
    dispatch(updateShowSendPaymentLinkDrawer());
  };

  const validateForm = () => {
    if (!amount || parseFloat(amount) <= 0) {
      message.error("Please enter a valid amount");
      return false;
    }
    if (!phoneNumber) {
      message.error("Please enter a phone number");
      return false;
    }
    return true;
  };

  const handleSendPaymentLink = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        bookingId: actualBookingDetails?._id || actualBookingDetails?.id,
        hotelId: bookingHotelId,
        amount: parseFloat(amount),
        phoneNumber,
        countryCode,
        whatsappTemplate: "enquiry_booking",
        sendViaWhatsApp: true,
        email: email,
      };

      const response = await paymentLinkService.createPaymentLink(payload);

      if (response.paymentLink) {
        message.success("Payment link sent successfully!");
        message.info(`Payment link: ${response.paymentLink}`, 10);
        handleClose();
      } else {
        throw new Error(response.message || "Failed to create payment link");
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
      message.error(error.message || "Failed to send payment link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      sx={{
        "& .MuiDrawer-root": {
          position: "absolute",
          marginLeft: "auto",
          right: 0,
        },
        "& .MuiDrawer-paper": {
          position: "absolute",
          marginLeft: "auto",
          width: "800px",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          overflow: "visible",
          zIndex: 1300,
        },
      }}
      anchor={"bottom"}
      open={showSendPaymentLinkDrawer}
      onClose={handleClose}
    >
      <div className="m-0 p-0 pb-20">
        <div className="flex w-[100%] justify-between items-center px-3 py-3">
          <h2 className="font-[600] text-[1rem] text-fg font-body">
            Send Payment Link
          </h2>

          <div onClick={handleClose} className="cursor-pointer">
            <IoIosCloseCircle />
          </div>
        </div>

        <div className="flex w-full p-4 gap-4 justify-start border-t-[10px] border-t-brand-border-muted flex-col space-y-4">
          {/* First Row: Amount and Phone Number */}
          <div className="flex w-full gap-4">
            <div className="w-[50%]">
              <CustomTextField
                label="Amount"
                isMandatory={true}
                value={amount}
                onChange={(value) => setAmount(value)}
                type="number"
                inputPadding={true}
                inputPaddingValue={"3px"}
                labelTextColor={"rgb(var(--color-text-placeholder))"}
                placeholder="Enter amount"
                disabled={loading}
              />
            </div>

            <div className="w-[50%]">
              <div className="relative">
                <label className="absolute -top-2 left-3 bg-surface px-1 text-xs text-fg-placeholder z-10">
                  Phone Number <span className="text-danger">*</span>
                </label>
                <PhoneInput
                  key={`phone-input-${phoneInputKey}`}
                  defaultCountry="in"
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  disabled={loading}
                  forceDialCode={true}
                  inputStyle={{
                    width: "100%",
                    border: "1px solid rgb(var(--color-neutral-300))",
                    borderRadius: "4px",
                    fontSize: "14px",
                    outline: "none",
                    height: "40px",
                    fontFamily: "inherit",
                  }}
                  containerStyle={{
                    width: "100%",
                    height: "40px",
                    position: "relative",
                    zIndex: 1,
                  }}
                  dropdownStyle={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    zIndex: 9999,
                    position: "absolute",
                  }}
                  buttonStyle={{
                    border: "1px solid rgb(var(--color-neutral-300))",
                    borderRight: "none",
                    backgroundColor: "transparent",
                    borderRadius: "4px 0 0 4px",
                    height: "40px",
                    width: "52px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Second Row: Email */}
          <div className="flex w-full">
            <div className="w-full">
              <CustomTextField
                label="Email"
                isMandatory={true}
                value={email}
                onChange={(value) => setEmail(value)}
                type="email"
                inputPadding={true}
                inputPaddingValue={"3px"}
                labelTextColor={"rgb(var(--color-text-placeholder))"}
                placeholder="Enter email"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div
          className={`bg-surface flex justify-start items-center p-2 py-3 absolute border-t-[2px] border-t-brand-border-muted bottom-0 w-full cursor-pointer`}
          onClick={handleSendPaymentLink}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <div className="font-[500] cursor-pointer py-[12px] px-[24px] rounded-[8px] font-body text-fg-inverse bg-primary text-[18px] leading-[18px]">
              Send Payment Link
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
